<template>
  <div
    ref="dockEl"
    class="dock-container"
    :class="{ 'dock-mobile': isMobile }"
    data-tour="app-dock"
  >
    <nav
      class="dock"
      role="navigation"
      aria-label="Main navigation"
      :aria-describedby="!isMobile ? 'dock-shortcuts-hint' : undefined"
    >
      <!-- Main navigation items -->
      <div
        v-for="item in visibleItems"
        :key="item.path"
        class="dock-item-wrapper"
        @mouseenter="!isMobile && (hoveredItem = item.id)"
        @mouseleave="!isMobile && (hoveredItem = null)"
      >
        <RouterLink
          :to="item.path"
          class="dock-item"
          :class="{
            active: isActive(item.path),
            'has-submenu': item.subItems && item.subItems.length > 0,
          }"
          :aria-label="`${item.label} (Alt+${visibleItems.indexOf(item) + 1})`"
          :title="`${item.label} (Alt+${visibleItems.indexOf(item) + 1})`"
          @click="handleItemClick(item)"
        >
          <i :class="[item.icon, 'dock-icon']"></i>
          <span v-if="isMobile" class="dock-label">{{ item.shortLabel || item.label }}</span>
          <span v-if="item.badge" class="dock-badge">{{ item.badge }}</span>
          <i
            v-if="!isMobile && item.subItems && item.subItems.length > 0"
            class="pi pi-chevron-up dock-caret"
            :class="{ rotated: hoveredItem === item.id }"
          ></i>
        </RouterLink>

        <!-- Desktop flyout submenu -->
        <Transition name="flyout">
          <div
            v-if="!isMobile && hoveredItem === item.id && item.subItems && item.subItems.length > 0"
            class="dock-flyout"
          >
            <RouterLink
              v-for="subItem in item.subItems"
              :key="subItem.path"
              :to="subItem.path"
              class="flyout-item"
              @click="hoveredItem = null"
            >
              <i :class="[subItem.icon, 'flyout-icon']"></i>
              <span class="flyout-label">{{ subItem.label }}</span>
            </RouterLink>
          </div>
        </Transition>
      </div>

      <!-- More menu for overflow items on mobile -->
      <div v-if="isMobile && overflowItems.length > 0" class="dock-item-wrapper">
        <button
          class="dock-item dock-more"
          :class="{ active: isMoreMenuOpen }"
          @click="toggleMoreMenu"
          aria-label="More options"
        >
          <i class="pi pi-ellipsis-h dock-icon"></i>
          <span class="dock-label">{{ t('navigation.more') || 'More' }}</span>
        </button>
      </div>

      <!-- Desktop: Search trigger -->
      <template v-if="!isMobile && !demo">
        <div class="dock-separator"></div>
        <button
          class="dock-item dock-action"
          @click="openCommandPalette"
          :title="searchLabel"
          aria-label="Search"
        >
          <i class="pi pi-search dock-icon"></i>
        </button>
      </template>
    </nav>

    <!-- Screen reader hint for keyboard shortcuts -->
    <span id="dock-shortcuts-hint" class="sr-only" v-if="!isMobile">
      Use Alt + number keys 1-{{ allItems.length }} to quickly navigate to menu items
    </span>

    <!-- Mobile bottom sheet for overflow items -->
    <Transition name="sheet">
      <div v-if="isMobile && isMoreMenuOpen" class="mobile-sheet-overlay" @click="closeMoreMenu">
        <div class="mobile-sheet" @click.stop>
          <div class="sheet-header">
            <span class="sheet-title">{{ t('navigation.more') || 'More' }}</span>
            <button class="sheet-close" @click="closeMoreMenu" aria-label="Close">
              <i class="pi pi-times"></i>
            </button>
          </div>
          <div class="sheet-content">
            <RouterLink
              v-for="item in overflowItems"
              :key="item.path"
              :to="item.path"
              class="sheet-item"
              @click="closeMoreMenu"
            >
              <i :class="[item.icon, 'sheet-icon']"></i>
              <span class="sheet-label">{{ item.label }}</span>
              <i class="pi pi-chevron-right sheet-chevron"></i>
            </RouterLink>

            <!-- Sub-items for overflow -->
            <template v-for="item in overflowItems" :key="'sub-' + item.id">
              <template v-if="item.subItems && item.subItems.length > 0">
                <div class="sheet-divider"></div>
                <RouterLink
                  v-for="subItem in item.subItems"
                  :key="subItem.path"
                  :to="subItem.path"
                  class="sheet-item sheet-sub-item"
                  @click="closeMoreMenu"
                >
                  <i :class="[subItem.icon, 'sheet-icon']"></i>
                  <span class="sheet-label">{{ subItem.label }}</span>
                </RouterLink>
              </template>
            </template>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Mobile: Expanded submenu panel -->
    <Transition name="panel">
      <div
        v-if="isMobile && activeSubmenu"
        class="mobile-submenu-overlay"
        @click="activeSubmenu = null"
      >
        <div class="mobile-submenu" @click.stop>
          <div class="submenu-header">
            <button class="submenu-back" @click="activeSubmenu = null" aria-label="Back">
              <i class="pi pi-arrow-left"></i>
            </button>
            <span class="submenu-title">{{ activeSubmenu.label }}</span>
          </div>
          <div class="submenu-content">
            <RouterLink
              v-for="subItem in activeSubmenu.subItems"
              :key="subItem.path"
              :to="subItem.path"
              class="submenu-item"
              @click="activeSubmenu = null"
            >
              <i :class="[subItem.icon, 'submenu-icon']"></i>
              <span class="submenu-label">{{ subItem.label }}</span>
            </RouterLink>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from '@/composables/useI18n'
import { useCommandPalette } from '@/composables/useCommandPalette'
import { usePreferences } from '@/composables/usePreferences'
import { useFeatureFlags } from '@/composables/useFeatureFlags'
import { useAuthStore } from '@/stores/auth'
import type { UserRole } from '@/types/permissions'

const props = defineProps<{ demo?: boolean }>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { open: openCommandPalette } = useCommandPalette()
const { showAdvancedModules } = usePreferences()
const { liveShoppingEnabled } = useFeatureFlags()

const userRole = computed(() => authStore.user?.role as UserRole | undefined)

const hoveredItem = ref<string | null>(null)
const isMobile = ref(false)
const isMoreMenuOpen = ref(false)
const activeSubmenu = ref<DockItem | null>(null)

// Publish the dock's real footprint (height + its bottom offset + breathing
// room) as a CSS var so page content can reserve exactly enough space and the
// fixed dock never covers pagination/cards. Measured, not guessed.
const dockEl = ref<HTMLElement | null>(null)
let dockObserver: ResizeObserver | null = null

const publishDockClearance = () => {
  const el = dockEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const bottomGap = window.innerHeight - rect.bottom // distance from dock to viewport bottom
  const clearance = Math.ceil(rect.height + Math.max(bottomGap, 0) + 24)
  document.documentElement.style.setProperty('--app-dock-clearance', `${clearance}px`)
}

// Mobile breakpoint
const MOBILE_BREAKPOINT = 768
const MAX_MOBILE_ITEMS = 5 // Increased from 4 for better mobile experience

const checkMobile = () => {
  isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT
  if (!isMobile.value) {
    isMoreMenuOpen.value = false
    activeSubmenu.value = null
  }
}

// Keyboard shortcuts for navigation
const handleKeyPress = (event: KeyboardEvent) => {
  // Alt + number key (1-9) for quick navigation
  if (event.altKey && !event.ctrlKey && !event.shiftKey) {
    const num = parseInt(event.key)
    if (num >= 1 && num <= allItems.value.length) {
      event.preventDefault()
      const item = allItems.value[num - 1]
      if (item) {
        router.push(item.path)
      }
    }
  }
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  window.addEventListener('keydown', handleKeyPress)
  publishDockClearance()
  if (dockEl.value && 'ResizeObserver' in window) {
    dockObserver = new ResizeObserver(() => publishDockClearance())
    dockObserver.observe(dockEl.value)
  }
  window.addEventListener('resize', publishDockClearance)
  // Capture phase so scrolls inside the inner content container are caught too.
  window.addEventListener('scroll', closeFlyoutOnScroll, { capture: true, passive: true })
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  window.removeEventListener('keydown', handleKeyPress)
  window.removeEventListener('resize', publishDockClearance)
  window.removeEventListener('scroll', closeFlyoutOnScroll, { capture: true })
  dockObserver?.disconnect()
  document.documentElement.style.removeProperty('--app-dock-clearance')
})

// Close menus on route change (desktop hover flyout included, so it can't linger
// over the next page's content).
watch(
  () => route.path,
  () => {
    isMoreMenuOpen.value = false
    activeSubmenu.value = null
    hoveredItem.value = null
  },
)

// Close the desktop hover flyout on any scroll: it floats above the dock over the
// bottom of the page, so a stuck-open flyout would intercept clicks on the last
// rows / footer controls beneath it.
const closeFlyoutOnScroll = () => {
  if (hoveredItem.value) hoveredItem.value = null
}

const searchLabel = computed(() => {
  const isMacOS =
    typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
  return `${t('commandPalette.placeholder').split('...')[0]} (${isMacOS ? '⌘' : 'Ctrl'}K)`
})

interface DockSubItem {
  path: string
  label: string
  icon: string
}

interface DockItem {
  id: string
  path: string
  label: string
  shortLabel?: string
  icon: string
  badge?: number
  subItems?: DockSubItem[]
}

// Base items definition with role restrictions
const baseItems = computed<DockItem[]>(() => {
  const role = userRole.value
  const advanced = showAdvancedModules.value
  const items: DockItem[] = [
    {
      id: 'dashboard',
      path: '/dashboard',
      label: t('navigation.dashboard'),
      shortLabel: 'Home',
      icon: 'pi pi-home',
    },
    {
      id: 'orders',
      path: '/orders',
      label: t('navigation.orders'),
      shortLabel: 'Orders',
      icon: 'pi pi-shopping-cart',
      subItems: [
        { path: '/orders', label: t('order.viewList'), icon: 'pi pi-list' },
        { path: '/orders/board', label: t('navigation.ordersBoard'), icon: 'pi pi-th-large' },
        // Only show order creation to admin/sales
        ...(role && ['admin', 'sales'].includes(role)
          ? [
              { path: '/orders/new', label: t('order.addOrder'), icon: 'pi pi-plus-circle' },
              { path: '/orders/quick', label: t('order.quickOrder'), icon: 'pi pi-bolt' },
            ]
          : []),
      ],
    },
    {
      id: 'inventory',
      path: '/products',
      label: t('navigation.groups.inventory'),
      shortLabel: 'Products',
      icon: 'pi pi-box', // Keep box for inventory
      subItems: [
        { path: '/products', label: t('navigation.products'), icon: 'pi pi-box' },
        // Only show "Add Product" to admin/sales
        ...(role && ['admin', 'sales'].includes(role)
          ? [{ path: '/products/new', label: t('product.addProduct'), icon: 'pi pi-plus-circle' }]
          : []),
        // Arrivals: advanced (consignment) module — admin/sales/accountant
        ...(advanced && role && ['admin', 'sales', 'accountant'].includes(role)
          ? [{ path: '/arrivals', label: t('navigation.arrivals'), icon: 'pi pi-inbox' }]
          : []),
      ],
    },
    {
      id: 'deliveries',
      path: '/deliveries',
      label: t('navigation.groups.logistics'),
      shortLabel: 'Logistics',
      icon: 'pi pi-send', // More action-oriented than truck
      subItems: [
        { path: '/deliveries', label: t('navigation.deliveries'), icon: 'pi pi-list' },
        { path: '/deliveries/kanban', label: t('navigation.kanban'), icon: 'pi pi-th-large' },
        // Only show Deliverer Performance to admin/sales
        ...(role && ['admin', 'sales'].includes(role)
          ? [
              {
                path: '/deliveries/performance',
                label: t('navigation.delivererPerformance'),
                icon: 'pi pi-chart-line',
              },
            ]
          : []),
        // Shippers + Shipments: advanced (cross-city) modules
        ...(advanced
          ? [
              { path: '/shippers', label: t('navigation.shippers'), icon: 'pi pi-truck' },
              { path: '/shipments', label: t('navigation.shipments'), icon: 'pi pi-globe' },
            ]
          : []),
      ],
    },
    {
      id: 'customers',
      path: '/customers',
      label: t('navigation.customers'),
      shortLabel: 'Customers',
      icon: 'pi pi-users',
      subItems: [
        { path: '/customers', label: t('navigation.customers'), icon: 'pi pi-list' },
        // Only show "New Customer" to admin/sales
        ...(role && ['admin', 'sales'].includes(role)
          ? [
              {
                path: '/customers/new',
                label: t('navigation.newCustomer'),
                icon: 'pi pi-user-plus',
              },
            ]
          : []),
      ],
    },
  ]

  // Live Shopping — gated by org feature flag (TikTok/FB live sellers only).
  // Boutiques + restaurants don't see this surface.
  if (liveShoppingEnabled.value && role && ['admin', 'sales', 'accountant'].includes(role)) {
    items.push({
      id: 'live-shopping',
      path: '/live-sessions',
      label: t('navigation.liveShopping'),
      shortLabel: 'Live',
      icon: 'pi pi-video',
      subItems: [
        { path: '/live-sessions', label: t('navigation.liveSessions'), icon: 'pi pi-list' },
        ...(role && ['admin', 'sales'].includes(role)
          ? [
              {
                path: '/live-sessions/new',
                label: t('navigation.newLiveSession'),
                icon: 'pi pi-plus-circle',
              },
            ]
          : []),
      ],
    })
  }

  // Only show finance (payments/settlements) to admin/sales/accountant (deliverers blocked)
  if (role && ['admin', 'sales', 'accountant'].includes(role)) {
    items.push({
      id: 'finance',
      path: '/payments',
      label: t('navigation.groups.finance'),
      shortLabel: 'Finance',
      icon: 'pi pi-wallet',
      subItems: [
        { path: '/payments', label: t('navigation.payments'), icon: 'pi pi-credit-card' },
        // Settlements: advanced (consignment payout) — admin/accountant
        ...(advanced && role && ['admin', 'accountant'].includes(role)
          ? [{ path: '/settlements', label: t('navigation.settlements'), icon: 'pi pi-file-check' }]
          : []),
      ],
    })
  }

  // Only show settings to admin (org config, team members, payment methods, zones)
  if (role === 'admin') {
    items.push({
      id: 'settings',
      path: '/settings',
      label: t('navigation.settings'),
      shortLabel: 'Settings',
      icon: 'pi pi-cog',
    })
  }

  return items
})

// Focused nav for the live demo: only the showcased, safe-to-expose modules
// (deliveries, orders, customers). Cross-city is intentionally absent.
const demoItems = computed<DockItem[]>(() => [
  {
    id: 'deliveries',
    path: '/deliveries/kanban',
    label: t('navigation.deliveries'),
    shortLabel: 'Livraisons',
    icon: 'pi pi-send',
    subItems: [
      { path: '/deliveries/kanban', label: t('navigation.kanban'), icon: 'pi pi-th-large' },
      { path: '/deliveries', label: t('navigation.deliveries'), icon: 'pi pi-list' },
    ],
  },
  {
    id: 'orders',
    path: '/orders',
    label: t('navigation.orders'),
    shortLabel: 'Ventes',
    icon: 'pi pi-shopping-cart',
    subItems: [
      { path: '/orders', label: t('order.viewList'), icon: 'pi pi-list' },
      { path: '/orders/quick', label: t('order.quickOrder'), icon: 'pi pi-bolt' },
    ],
  },
  {
    id: 'customers',
    path: '/customers',
    label: t('navigation.customers'),
    shortLabel: 'Clients',
    icon: 'pi pi-users',
    subItems: [
      { path: '/customers', label: t('navigation.customers'), icon: 'pi pi-list' },
      { path: '/customers/new', label: t('navigation.newCustomer'), icon: 'pi pi-user-plus' },
    ],
  },
  // Cross-city (shipments/shippers) is intentionally omitted from the demo dock:
  // those screens expose the proprietary zone -> shipper-office bucket routing.
  // The router also hard-blocks these paths in embedded-demo mode (see router).
])

const allItems = computed<DockItem[]>(() => (props.demo ? demoItems.value : baseItems.value))

// Split items for mobile view
const visibleItems = computed(() => {
  if (!isMobile.value) return allItems.value
  return allItems.value.slice(0, MAX_MOBILE_ITEMS)
})

const overflowItems = computed(() => {
  if (!isMobile.value) return []
  return allItems.value.slice(MAX_MOBILE_ITEMS)
})

const isActive = (path: string): boolean => {
  return route.path === path || route.path.startsWith(path + '/')
}

const handleItemClick = (item: DockItem) => {
  if (isMobile.value && item.subItems && item.subItems.length > 0) {
    activeSubmenu.value = item
  }
}

const toggleMoreMenu = () => {
  isMoreMenuOpen.value = !isMoreMenuOpen.value
}

const closeMoreMenu = () => {
  isMoreMenuOpen.value = false
}
</script>

<style scoped>
/* Screen reader only - accessibility helper */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Container */
.dock-container {
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  padding: 0 1rem;
}

/* Desktop dock */
.dock {
  display: flex;
  align-items: center;
  gap: 0.125rem; /* Reduced from 0.25rem for more compact layout */
  padding: 0.375rem 0.625rem; /* Slightly reduced padding */
  background: var(--glass-bg-heavy);
  backdrop-filter: blur(var(--glass-blur-xl));
  -webkit-backdrop-filter: blur(var(--glass-blur-xl));
  border: 1px solid var(--glass-border);
  border-radius: 1rem;
  box-shadow:
    var(--glass-shadow-lg),
    0 8px 32px rgba(0, 0, 0, 0.12);
  max-width: calc(100vw - 2rem); /* Prevent overflow on small screens */
}

.app-dark .dock {
  background: var(--glass-bg-heavy);
  border-color: var(--glass-border);
  box-shadow:
    var(--glass-shadow-lg),
    0 8px 32px rgba(0, 0, 0, 0.4);
}

.dock-item-wrapper {
  position: relative;
}

.dock-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem; /* Slightly reduced from 3rem */
  height: 2.75rem;
  border-radius: 0.625rem; /* Slightly smaller radius */
  color: var(--app-text-secondary);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1); /* Faster transition */
  text-decoration: none;
}

.dock-item:hover {
  background: var(--app-surface-100);
  color: var(--app-text-primary);
  transform: translateY(-3px) scale(1.08); /* Slightly less aggressive */
}

.app-dark .dock-item:hover {
  background: var(--app-surface-700);
  color: var(--app-surface-0);
}

.dock-item.active {
  background: var(--app-primary-50);
  color: var(--app-primary-text);
}

.app-dark .dock-item.active {
  background: var(--app-primary-900);
  color: var(--app-primary-text);
}

.dock-item.active::after {
  content: '';
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--app-primary-color);
}

.dock-icon {
  font-size: 1.125rem; /* Slightly reduced from 1.25rem for more compact layout */
}

.dock-caret {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 0.5rem;
  color: var(--app-text-muted);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
  opacity: 0.7;
}

.dock-item.has-submenu:hover .dock-caret {
  opacity: 1;
  color: var(--app-text-secondary);
}

.dock-caret.rotated {
  transform: rotate(180deg);
  opacity: 1;
  color: var(--app-primary-text);
}

.dock-label {
  display: none;
}

.dock-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.625rem;
  font-weight: 600;
  background: var(--app-danger-500);
  color: white;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dock-separator {
  width: 1px;
  height: 1.75rem; /* Slightly reduced height */
  background: var(--app-border);
  margin: 0 0.125rem; /* Reduced margin for compactness */
  opacity: 0.5;
}

.dock-action {
  color: var(--app-text-muted);
}

.dock-action:hover {
  color: var(--app-primary-text);
}

/* Desktop flyout */
.dock-flyout {
  position: absolute;
  bottom: calc(100% + 0.625rem);
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  padding: 0.625rem;
  background: var(--glass-bg-heavy);
  backdrop-filter: blur(var(--glass-blur-lg));
  -webkit-backdrop-filter: blur(var(--glass-blur-lg));
  border: 1px solid var(--glass-border);
  border-radius: 0.875rem;
  box-shadow:
    var(--glass-shadow-lg),
    0 12px 40px rgba(0, 0, 0, 0.15);
  z-index: 10;
  overflow: hidden;
}

/* Arrow pointing down to dock item */
.dock-flyout::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 12px;
  height: 12px;
  background: var(--glass-bg-heavy);
  border-right: 1px solid var(--glass-border);
  border-bottom: 1px solid var(--glass-border);
  transform: translateX(-50%) rotate(45deg);
  backdrop-filter: blur(var(--glass-blur-lg));
  -webkit-backdrop-filter: blur(var(--glass-blur-lg));
}

.app-dark .dock-flyout {
  background: var(--app-surface-900);
  border-color: var(--app-surface-700);
}

.app-dark .dock-flyout::after {
  background: var(--app-surface-900);
  border-color: var(--app-surface-700);
}

.flyout-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  width: 100%;
  padding: 0.75rem 0.875rem;
  border-radius: 0.625rem;
  color: var(--app-text-secondary);
  text-decoration: none;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  position: relative;
  font-weight: 500;
}

.flyout-item::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 0.5rem;
  background: var(--app-primary-color);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.flyout-item:hover::before {
  opacity: 0.08;
}

.flyout-item:hover {
  background: var(--app-surface-100);
  color: var(--app-text-primary);
  transform: translateX(2px);
}

.app-dark .flyout-item:hover {
  background: var(--app-surface-800);
  color: var(--app-surface-0);
}

.flyout-icon {
  font-size: 0.875rem;
  width: 1rem;
  text-align: center;
  color: var(--app-text-muted);
  position: relative;
  z-index: 1;
  transition: color 0.15s ease;
}

.flyout-item:hover .flyout-icon {
  color: var(--app-primary-text);
}

.flyout-item:first-child {
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.flyout-item:not(:first-child) {
  margin-top: 0.125rem;
}

.flyout-label {
  white-space: nowrap;
  position: relative;
  z-index: 1;
}

/* Flyout animation */
.flyout-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); /* Spring-like easing */
}

.flyout-leave-active {
  transition: all 0.15s cubic-bezier(0.4, 0, 1, 1);
}

.flyout-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(12px) scale(0.95);
}

.flyout-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px) scale(0.98);
}

/* ============================================
   MOBILE STYLES
   ============================================ */

.dock-mobile {
  bottom: 0;
  left: 0;
  right: 0;
  transform: none;
  padding: 0;
}

.dock-mobile .dock {
  width: 100%;
  /* Reset the desktop pill's max-width so the bar spans the full viewport on
     phones (otherwise it caps at ~362px, leaving dead gaps each side). */
  max-width: none;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-bottom: none;
  padding: 0.5rem 0;
  justify-content: space-around;
  gap: 0;
  padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0));
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  scrollbar-width: none; /* Hide scrollbar on Firefox */
  -ms-overflow-style: none; /* Hide scrollbar on IE/Edge */
  scroll-snap-type: x proximity; /* Add snap scrolling for better UX */
  scroll-behavior: smooth;
}

/* Hide scrollbar on webkit browsers */
.dock-mobile .dock::-webkit-scrollbar {
  display: none;
}

.dock-mobile .dock-item {
  flex-direction: column;
  width: auto;
  height: auto;
  padding: 0.5rem 0.75rem;
  gap: 0.25rem;
  border-radius: 0.5rem;
  scroll-snap-align: start; /* Snap to items when scrolling */
  flex-shrink: 0; /* Prevent items from shrinking */
}

.dock-mobile .dock-item:hover {
  transform: none;
}

.dock-mobile .dock-item:active {
  transform: scale(0.95);
  background: var(--app-surface-100);
}

.app-dark .dock-mobile .dock-item:active {
  background: var(--app-surface-700);
}

.dock-mobile .dock-item.active::after {
  display: none;
}

.dock-mobile .dock-icon {
  font-size: 1.25rem;
}

.dock-mobile .dock-label {
  display: block;
  font-size: 0.625rem;
  font-weight: 500;
  white-space: nowrap;
}

.dock-mobile .dock-more {
  background: transparent;
}

/* Mobile bottom sheet */
.mobile-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: flex-end;
}

.mobile-sheet {
  width: 100%;
  max-height: 70vh;
  background: var(--app-surface-0);
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-dark .mobile-sheet {
  background: var(--app-surface-900);
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--app-border);
}

.sheet-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
}

.sheet-close {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 50%;
  background: var(--app-surface-100);
  color: var(--app-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-dark .sheet-close {
  background: var(--app-surface-800);
}

.sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0));
}

.sheet-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  color: var(--app-text-primary);
  text-decoration: none;
  transition: background 0.15s;
}

.sheet-item:active {
  background: var(--app-surface-100);
}

.app-dark .sheet-item:active {
  background: var(--app-surface-800);
}

.sheet-sub-item {
  padding-left: 2.5rem;
  color: var(--app-text-secondary);
}

.sheet-icon {
  font-size: 1.25rem;
  width: 1.5rem;
  text-align: center;
  color: var(--app-text-muted);
}

.sheet-label {
  flex: 1;
  font-size: 0.9375rem;
}

.sheet-chevron {
  font-size: 0.75rem;
  color: var(--app-text-muted);
}

.sheet-divider {
  height: 1px;
  background: var(--app-border);
  margin: 0.5rem 1rem;
}

/* Mobile submenu panel */
.mobile-submenu-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1500;
  display: flex;
  align-items: flex-end;
}

.mobile-submenu {
  width: 100%;
  max-height: 60vh;
  background: var(--app-surface-0);
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
}

.app-dark .mobile-submenu {
  background: var(--app-surface-900);
}

.submenu-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid var(--app-border);
}

.submenu-back {
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 0.75rem;
  background: var(--app-surface-100);
  color: var(--app-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-dark .submenu-back {
  background: var(--app-surface-800);
}

.submenu-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
}

.submenu-content {
  padding: 0.5rem;
  padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0));
}

.submenu-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  color: var(--app-text-primary);
  text-decoration: none;
}

.submenu-item:active {
  background: var(--app-surface-100);
}

.app-dark .submenu-item:active {
  background: var(--app-surface-800);
}

.submenu-icon {
  font-size: 1.125rem;
  width: 1.5rem;
  text-align: center;
  color: var(--app-text-muted);
}

.submenu-label {
  font-size: 0.9375rem;
}

/* Sheet animations */
.sheet-enter-active,
.sheet-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}

.sheet-enter-from .mobile-sheet,
.sheet-leave-to .mobile-sheet {
  transform: translateY(100%);
}

.panel-enter-active,
.panel-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.panel-enter-from,
.panel-leave-to {
  opacity: 0;
}

.panel-enter-from .mobile-submenu,
.panel-leave-to .mobile-submenu {
  transform: translateY(100%);
}

/* Tablet adjustments */
@media (min-width: 481px) and (max-width: 768px) {
  .dock-mobile .dock {
    padding: 0.625rem 1rem;
    justify-content: center;
    gap: 0.5rem;
  }

  .dock-mobile .dock-item {
    padding: 0.625rem 1rem;
  }

  .dock-mobile .dock-label {
    font-size: 0.6875rem;
  }
}

/* Small mobile */
@media (max-width: 360px) {
  .dock-mobile .dock-item {
    padding: 0.375rem 0.5rem;
  }

  .dock-mobile .dock-icon {
    font-size: 1.125rem;
  }

  .dock-mobile .dock-label {
    font-size: 0.5625rem;
  }
}

/* Landscape mobile */
@media (max-height: 500px) and (max-width: 768px) {
  .dock-mobile .dock {
    padding: 0.375rem 0;
  }

  .dock-mobile .dock-item {
    flex-direction: row;
    padding: 0.375rem 0.75rem;
    gap: 0.375rem;
  }

  .dock-mobile .dock-icon {
    font-size: 1rem;
  }

  .dock-mobile .dock-label {
    font-size: 0.6875rem;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .dock-item,
  .dock-caret,
  .flyout-item,
  .flyout-enter-active,
  .flyout-leave-active,
  .sheet-enter-active,
  .sheet-leave-active,
  .panel-enter-active,
  .panel-leave-active {
    transition: none;
  }

  .dock-item:hover {
    transform: none;
  }

  .flyout-item:hover {
    transform: none;
  }

  .dock-caret.rotated {
    transform: none;
  }
}

/* Focus states */
.dock-item:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 2px;
  background: var(--app-surface-100);
  transform: translateY(-2px) scale(1.05);
}

.app-dark .dock-item:focus-visible {
  background: var(--app-surface-700);
}

.sheet-item:focus-visible,
.submenu-item:focus-visible,
.flyout-item:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: -2px;
  background: var(--app-surface-100);
}

.app-dark .sheet-item:focus-visible,
.app-dark .submenu-item:focus-visible,
.app-dark .flyout-item:focus-visible {
  background: var(--app-surface-800);
}
</style>
